import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Shield, Home as HomeIcon, Phone, MessageCircle } from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import { openChat } from '../../utils/cn';

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// ─── Material Warranties data (per the warranty page mockup) ────────
//
// 4 manufacturer cards stacked vertically. TAMKO is the featured one
// (PLATINUM ribbon badge + cyan top accent). Brand logos sourced from
// /public/assets/images/.

const WARRANTY_CARDS = [
  {
    brand: 'TAMKO',
    featured: true,
    logoSrc: '/assets/images/TAMKO_3D_logo-1767901576132.jpg',
    warrantyUrl:
      'https://www.tamko.com/docs/default-source/enhanced-limited-system-warranties/tamko-mastershield-enhanced-limited-system-warranty.pdf',
    warrantyName: 'MasterShield® Enhanced Limited System Warranty',
    body: (
      <>
        OpenbooQ's TAMKO Pro Platinum certification qualifies your TAMKO Titan XT® install for TAMKO's Enhanced Limited System Warranty — adding manufacturer-backed workmanship coverage on top of our own 5-year workmanship guarantee.
      </>
    ),
    stats: [
      { value: 'Lifetime', label: 'Limited material term (single-family home)' },
      { value: '50 yr', label: 'Full non-prorated coverage period' },
      { value: '10 yr', label: 'TAMKO workmanship coverage' },
    ],
    chips: [
      { label: '✓ Transferable once', variant: 'green' },
      { label: 'Up to 160 mph wind protection', variant: 'blue' },
      { label: '10-year algae cleaning coverage', variant: 'pink' },
    ],
    disclaimer:
      'Coverage requires complete tear-off and qualifying TAMKO accessory products (included with every OpenbooQ install). Lifetime term applies to owner-occupied single-family homes.',
  },
  {
    brand: 'GAF',
    featured: false,
    logoSrc: '/assets/images/GAF_3D_logo_1_-1767901680135.jpg',
    warrantyUrl: 'https://www.gaf.com/en-us/resources/warranties/gaf-roofing-system',
    warrantyName: 'Roofing System Limited Warranty',
    body: (
      <>
        OpenbooQ installs of GAF Timberline® HDZ® shingles — from North America's largest roofing manufacturer — qualify for GAF's full Roofing System Limited Warranty, paired with our standard 5-year workmanship guarantee.
      </>
    ),
    stats: [
      { value: 'Lifetime', label: 'Limited material term (single-family home)' },
      { value: '10 yr', label: 'Smart Choice® non-prorated coverage' },
      { value: '15 yr', label: 'Wind warranty term' },
    ],
    chips: [
      { label: '✓ Transferable once', variant: 'green' },
      { label: 'Up to 130 mph wind protection', variant: 'blue' },
      { label: '25-yr StainGuard Plus™ algae protection', variant: 'pink' },
    ],
    disclaimer:
      'Coverage requires installation with qualifying GAF accessory products (included with every OpenbooQ install). Lifetime term applies to owner-occupied single-family homes.',
  },
  {
    brand: 'Owens Corning',
    featured: false,
    logoSrc: '/assets/images/Owens_Corning_3D_logo_1_-1767901713667.jpg',
    warrantyUrl: 'https://www.owenscorning.com/en-us/roofing/warranty',
    warrantyName: 'Standard Product Limited Lifetime Warranty',
    body: (
      <>
        OpenbooQ installs of Owens Corning® Oakridge® and Duration® shingles qualify for Owens Corning's Standard Product Limited Lifetime Warranty, paired with the complete Owens Corning roofing system and our 5-year workmanship guarantee.
      </>
    ),
    stats: [
      { value: 'Lifetime', label: 'Limited material term (single-family home)' },
      { value: '10 yr', label: 'TRU PROtection® non-prorated period' },
      { value: '15 yr', label: 'Blow off (wind) warranty term' },
    ],
    chips: [
      { label: '✓ Transferable once', variant: 'green' },
      { label: 'Up to 130 mph wind protection', variant: 'blue' },
      { label: '25-yr StreakGuard® algae protection', variant: 'pink' },
    ],
    disclaimer:
      'Coverage requires installation with complete Owens Corning® roofing components (included with every OpenbooQ install). Lifetime term applies to owner-occupied single-family homes.',
  },
  {
    brand: 'Atlas',
    featured: false,
    logoSrc: '/assets/images/atlas-logo.webp',
    warrantyUrl: 'https://www.atlasroofing.com/shingles-warranty',
    warrantyName: 'Limited Lifetime Warranty',
    body: (
      <>
        OpenbooQ installs of Atlas® Pinnacle® Pristine shingles qualify for Atlas's Limited Lifetime Warranty — featuring Class 3 impact resistance and lifetime 3M™ Scotchgard™ algae protection, paired with our standard 5-year workmanship guarantee.
      </>
    ),
    stats: [
      { value: 'Lifetime', label: 'Limited material term (single-family home)' },
      { value: '10 yr', label: 'Premium Protection non-prorated period' },
      { value: '15 yr', label: 'Wind warranty term' },
    ],
    chips: [
      { label: '✓ Transferable once', variant: 'green' },
      { label: 'Up to 130 mph wind protection', variant: 'blue' },
      { label: 'Lifetime Scotchgard™ algae protection', variant: 'pink' },
    ],
    disclaimer:
      'No eligibility requirements — standard warranty accompanies every Atlas® shingle purchase. Lifetime term applies to owner-occupied single-family homes. Lifetime algae protection applies to Scotchgard™-branded shingles and ridge cap.',
  },
];

// Chip color styles by variant.
const CHIP_STYLES = {
  green: { bg: '#d1fae5', color: '#047857', border: '1px solid #a7f3d0' },
  blue:  { bg: '#e8f5fd', color: '#0a6eb8', border: '1px solid #bae6fd' },
  pink:  { bg: '#ffe4e6', color: '#9f1239', border: '1px solid #fecdd3' },
};

const WarrantyCard = ({ card, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.55, delay: 0.05 + index * 0.05, ease: EASE_OUT_EXPO }}
    whileHover={{ y: -3, transition: { duration: 0.25, ease: 'easeOut' } }}
    className="relative rounded-2xl bg-white overflow-hidden cursor-default"
    style={{
      border: card.featured ? '2px solid #07B0E9' : '1px solid #e5ebf0',
      boxShadow: card.featured
        ? '0 12px 32px rgba(7, 176, 233, 0.12), 0 0 0 4px rgba(7, 176, 233, 0.04)'
        : '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
    }}
  >
    {/* TAMKO PLATINUM ribbon — featured card only */}
    {card.featured && (
      <div
        className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-[0.1em]"
        style={{
          background: 'linear-gradient(135deg, #0a1628 0%, #2a3a4f 100%)',
          color: 'white',
          boxShadow: '0 4px 10px rgba(10, 22, 40, 0.25)',
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#07B0E9" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
        Platinum™
      </div>
    )}

    <div className={`p-6 lg:p-8 ${card.featured ? 'pt-16 lg:pt-16' : ''}`}>
      {/* Brand row: logo + name + warranty name */}
      <div className="flex items-start gap-4 mb-5">
        <img
          src={card.logoSrc}
          alt={`${card.brand} logo`}
          className="flex-shrink-0 rounded-md object-contain bg-white"
          style={{ width: '48px', height: '48px' }}
          loading="lazy"
        />
        <div className="flex-1 min-w-0">
          <h3
            className="text-2xl lg:text-3xl font-black leading-tight"
            style={{
              color: '#07B0E9',
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              letterSpacing: '-0.02em',
            }}
          >
            {card.brand}
          </h3>
          <p className="text-sm lg:text-base text-gray-600 mt-0.5">
            {card.warrantyName}
          </p>
        </div>
      </div>

      {/* Body */}
      <p className="text-sm lg:text-[15px] text-gray-700 leading-relaxed mb-6">
        {card.body}
      </p>

      {/* 3-stat row */}
      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-0 sm:gap-px mb-5 overflow-hidden rounded-xl"
        style={{ background: '#e5ebf0', border: '1px solid #e5ebf0' }}
      >
        {card.stats.map((stat) => (
          <div key={stat.label} className="bg-white p-4 lg:p-5">
            <div
              className="font-black leading-none mb-1.5"
              style={{
                fontSize: 'clamp(28px, 3.2vw, 38px)',
                color: '#07B0E9',
                letterSpacing: '-0.03em',
              }}
            >
              {stat.value}
            </div>
            <div className="text-xs lg:text-[13px] text-gray-600 leading-snug">
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* 3 chip pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        {card.chips.map((chip) => {
          const s = CHIP_STYLES[chip.variant] || CHIP_STYLES.blue;
          return (
            <span
              key={chip.label}
              className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: s.bg, color: s.color, border: s.border }}
            >
              {chip.label}
            </span>
          );
        })}
      </div>

      {/* Disclaimer + view link, separated by hairline */}
      <div className="pt-4" style={{ borderTop: '1px solid #e5ebf0' }}>
        <p className="text-xs lg:text-[13px] text-gray-500 leading-relaxed mb-3">
          {card.disclaimer}
        </p>
        <a
          href={card.warrantyUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-1 text-xs lg:text-sm font-semibold hover:underline"
          style={{ color: '#07B0E9' }}
        >
          View full warranty terms
          <span className="transition-transform duration-200 ease-out group-hover:translate-x-1">→</span>
        </a>
      </div>
    </div>
  </motion.article>
);

const WarrantyPage = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Roofing Warranty",
    "description": "Comprehensive roofing warranty covering materials and workmanship for all OpenbooQ roof replacement projects.",
    "provider": {
      "@id": "https://openbooq.com/#organization"
    },
    "serviceType": "Roofing Warranty",
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Standard Warranty",
        "description": "3-year full materials and workmanship coverage",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Included with every roof replacement"
        }
      },
      {
        "@type": "Offer",
        "name": "Premium Warranty",
        "description": "5-year enhanced protection with additional weather coverage, fully transferable",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "USD"
        }
      },
      {
        "@type": "Offer",
        "name": "Ultimate Warranty",
        "description": "10-year comprehensive care with lifetime material warranty, fully transferable",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "USD"
        }
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What exactly is covered under the OpenbooQ warranty?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our comprehensive warranty covers all materials and workmanship for your roofing project including all roofing materials (shingles, underlayment, flashing, gutters), professional installation and workmanship, leak prevention and waterproofing, structural integrity and proper attachment, and ventilation systems and moisture control."
        }
      },
      {
        "@type": "Question",
        "name": "How long does the warranty last?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer three warranty levels: Standard (3 Years) with full materials and workmanship coverage, Premium (5 Years) with enhanced protection and additional weather coverage, and Ultimate (10 Years) with comprehensive care and lifetime material warranty."
        }
      },
      {
        "@type": "Question",
        "name": "Is the warranty transferable if I sell my home?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Our Premium and Ultimate warranty levels are fully transferable to new homeowners. This can increase your home's value and appeal to potential buyers. To transfer, notify us at least 30 days in advance and complete a simple transfer form."
        }
      },
      {
        "@type": "Question",
        "name": "How do I file a warranty claim?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Filing a claim is simple and can be done 24/7 by calling our warranty hotline, submitting online, using our mobile app to upload photos and details, or emailing claims@openbooq.com. We'll respond within 24 hours and typically complete the entire claims process in 7-14 business days."
        }
      },
      {
        "@type": "Question",
        "name": "Do I have to pay anything for warranty repairs?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No! All covered warranty repairs are completely free, including all materials needed for the repair, professional labor and installation, inspection and quality assurance, and follow-up service if needed."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://openbooq.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Warranty",
        "item": "https://openbooq.com/warranty"
      }
    ]
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Roofing Warranty Coverage | OpenbooQ</title>
        <meta 
          name="description" 
          content="Protect your investment with OpenbooQ's comprehensive roofing warranties. Learn about our coverage options for shingles, materials, and workmanship." 
        />
        <meta name="keywords" content="roofing warranty, warranty protection, OpenbooQ warranty, shingle warranty, workmanship warranty, roof replacement warranty" />
        <meta property="og:title" content="Roofing Warranty Coverage | OpenbooQ" />
        <meta property="og:description" content="Comprehensive roofing warranty coverage for materials and workmanship. Learn how OpenbooQ protects your investment." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        {/* ═══ HERO ══════════════════════════════════════════════════
            Roof-construction photo background + dark navy overlay + blue
            grid pattern + cyan ambient glow. Cyan "WARRANTY COVERAGE"
            eyebrow pill, two-line headline (white "Your Roof." + cyan
            "Fully Protected."), subhead with bold highlights, and 3
            cyan-checkmark trust items. Same overlay vocabulary as the
            /how-it-works hero so text stays legible over the photo.
            Mobile padding pt-48 clears the floating header that can
            reach ~175px on notched iPhones. */}
        <section
          className="relative overflow-hidden pt-48 sm:pt-36 lg:pt-44 pb-20 lg:pb-28"
          style={{
            backgroundImage: `url('/assets/images/warranty-hero.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: '#0a1628',
          }}
        >
          {/* Dark navy overlay over the photo — keeps the white headline
              and subhead clearly legible while letting some texture from
              the roof photo come through. */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(135deg, rgba(10, 22, 40, 0.88) 0%, rgba(14, 30, 54, 0.82) 100%)',
            }}
          />
          {/* Cyan ambient glow center-top */}
          <div
            className="absolute pointer-events-none"
            aria-hidden="true"
            style={{
              top: '15%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '50%',
              height: '50%',
              background: 'radial-gradient(ellipse, rgba(7,176,233,0.18) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />

          <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center text-white">
            {/* Eyebrow pill */}
            <motion.div
              className="mb-8 flex justify-center"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                style={{
                  background: 'rgba(7, 176, 233, 0.18)',
                  border: '1px solid rgba(7, 176, 233, 0.45)',
                  color: '#6cc7ff',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#10B981' }}
                  aria-hidden="true"
                />
                Warranty Coverage
              </div>
            </motion.div>

            {/* Two-line headline: white "Your Roof." then cyan
                "Fully Protected." stacked. Each line animates in
                sequence (line 1 then line 2 with a short offset). */}
            <h1
              className="font-black leading-[1.0] tracking-tight mb-8"
              style={{
                fontSize: 'clamp(48px, 7vw, 96px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
              }}
            >
              <motion.span
                className="block"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.08, ease: EASE_OUT_EXPO }}
              >
                Your Roof.
              </motion.span>
              <motion.span
                className="block"
                style={{ color: '#07B0E9' }}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.22, ease: EASE_OUT_EXPO }}
              >
                Fully Protected.
              </motion.span>
            </h1>

            <motion.p
              className="mb-10 max-w-2xl mx-auto leading-relaxed"
              style={{
                fontSize: 'clamp(15px, 1.4vw, 18px)',
                color: 'rgba(255, 255, 255, 0.85)',
              }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.4, ease: EASE_OUT_EXPO }}
            >
              Every OpenbooQ roof is backed by{' '}
              <strong style={{ color: '#ffffff', fontWeight: 700 }}>manufacturer material warranties</strong>{' '}
              and our own{' '}
              <strong style={{ color: '#ffffff', fontWeight: 700 }}>workmanship guarantee</strong>{' '}
              — stacked so you're covered from the deck up.
            </motion.p>

            {/* 3 cyan-check trust items — stagger left to right */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm"
              style={{ color: 'rgba(255, 255, 255, 0.85)' }}
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.08, delayChildren: 0.55 } },
              }}
            >
              {[
                'Manufacturer-backed',
                'Workmanship guaranteed',
                'Transferable on sale',
              ].map((item) => (
                <motion.div
                  key={item}
                  className="flex items-center gap-2"
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT_EXPO } },
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#07B0E9"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{item}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ═══ THE OPENBOOQ PROMISE — 5-Year Workmanship Warranty
            Large cyan-gradient card on a light gray section bg. Subtle
            grid pattern overlay. Translucent white eyebrow pill, two-
            line headline (white + lighter cyan), body, and 3 glass
            stat boxes with cyan icons. Matches the mockup target. */}
        <motion.section
          className="py-16 lg:py-24"
          style={{ background: '#F8FAFC' }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: {} }}
        >
          <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE_OUT_EXPO } },
              }}
              className="rounded-3xl p-8 sm:p-10 lg:p-14 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
                boxShadow: '0 20px 60px rgba(7, 176, 233, 0.25)',
              }}
            >
              {/* Subtle white grid pattern overlay */}
              <div
                className="absolute inset-0 pointer-events-none"
                aria-hidden="true"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px),' +
                    ' linear-gradient(90deg, rgba(255, 255, 255, 0.07) 1px, transparent 1px)',
                  backgroundSize: '60px 60px',
                }}
              />

              <div className="relative">
                {/* Eyebrow pill — translucent white on cyan */}
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em] mb-6"
                  style={{
                    background: 'rgba(255, 255, 255, 0.18)',
                    border: '1px solid rgba(255, 255, 255, 0.30)',
                    color: 'rgba(255, 255, 255, 0.95)',
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: '#10B981' }}
                    aria-hidden="true"
                  />
                  The OpenbooQ Promise
                </div>

                {/* Two-line headline: white "5-Year Workmanship Warranty"
                    + lighter cyan "Standard With Every Roof" */}
                <h2
                  className="font-black leading-[1.05] tracking-tight mb-6 text-white"
                  style={{
                    fontSize: 'clamp(34px, 4.5vw, 56px)',
                    fontFamily: "'Poppins', 'Montserrat', sans-serif",
                    letterSpacing: '-0.025em',
                  }}
                >
                  5-Year Workmanship Warranty
                  <span className="block" style={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                    Standard With Every Roof
                  </span>
                </h2>

                <p
                  className="leading-relaxed mb-10 max-w-3xl"
                  style={{
                    fontSize: 'clamp(15px, 1.3vw, 18px)',
                    color: 'rgba(255, 255, 255, 0.88)',
                  }}
                >
                  If anything goes wrong with how your roof was installed, we fix it. No deductibles, no contractor finger-pointing, no chasing down a sub. One number to call — we handle the rest.
                </p>

                {/* 3 glass stat boxes */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
                  {[
                    { Icon: Shield, num: '5 yr', label: 'Standard workmanship coverage' },
                    { Icon: HomeIcon, num: 'Transferable', label: 'Transferable one time — stays with the home if you sell' },
                    { Icon: Phone, num: '1 call', label: 'We handle every claim end-to-end' },
                  ].map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.5, delay: 0.25 + i * 0.08, ease: EASE_OUT_EXPO }}
                      whileHover={{ y: -3, transition: { duration: 0.25, ease: 'easeOut' } }}
                      className="rounded-2xl p-5 lg:p-6 cursor-default"
                      style={{
                        background: 'rgba(255, 255, 255, 0.10)',
                        border: '1px solid rgba(255, 255, 255, 0.22)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                      }}
                    >
                      <stat.Icon
                        size={28}
                        strokeWidth={2.2}
                        color="white"
                        className="mb-3 opacity-90"
                        aria-hidden="true"
                      />
                      <div
                        className="font-black text-white leading-none mb-2"
                        style={{
                          fontSize: 'clamp(24px, 2.6vw, 32px)',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {stat.num}
                      </div>
                      <p className="text-sm leading-snug" style={{ color: 'rgba(255, 255, 255, 0.82)' }}>
                        {stat.label}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* ═══ MATERIAL WARRANTIES — 4-card stack per mockup ═════════
            Section eyebrow pill + headline + subhead, then 4 stacked
            warranty cards (TAMKO featured with Pro Platinum badge,
            then GAF, Owens Corning, Atlas). Each card: logo + brand
            + warranty name + body + 3-stat row + 3 chip pills +
            disclaimer + view-full-terms link. */}
        <motion.section
          className="py-16 lg:py-24"
          style={{ background: '#F8FAFC' }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          variants={{ hidden: {}, visible: {} }}
        >
          <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
            {/* Section header */}
            <div className="text-center mb-12 lg:mb-16">
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_OUT_EXPO } },
                }}
                className="mb-5 flex justify-center"
              >
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                  style={{
                    background: 'rgba(7, 176, 233, 0.10)',
                    border: '1px solid rgba(7, 176, 233, 0.30)',
                    color: '#0a6eb8',
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: '#10B981' }}
                    aria-hidden="true"
                  />
                  Material Warranties
                </div>
              </motion.div>
              <motion.h2
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
                }}
                className="font-black leading-[1.05] tracking-tight mb-4 mx-auto"
                style={{
                  fontSize: 'clamp(32px, 4.5vw, 52px)',
                  fontFamily: "'Poppins', 'Montserrat', sans-serif",
                  letterSpacing: '-0.025em',
                  color: '#0a1628',
                  maxWidth: '720px',
                }}
              >
                From the Industry's{' '}
                <span style={{ color: '#07B0E9' }}>Top Manufacturers</span>
              </motion.h2>
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
                }}
                className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed"
              >
                We're certified with the manufacturers we install. Higher certifications mean longer warranties and stronger coverage for you.
              </motion.p>
            </div>

            {/* 4 warranty cards stacked vertically */}
            <div className="space-y-6 lg:space-y-8">
              {WARRANTY_CARDS.map((card, i) => (
                <WarrantyCard key={card.brand} card={card} index={i} />
              ))}
            </div>
          </div>
        </motion.section>

        {/* ═══ HAVE A QUESTION ABOUT YOUR WARRANTY? — full-bleed cyan CTA
            Full-width gradient section. Centered headline + subhead, two
            CTA buttons (white "Contact Warranty Support" → openChat, and
            outline "Call (937) 910-8791" → tel:), plus single-line
            reassurance microcopy. Replaces 4 legacy v1 blocks. */}
        <motion.section
          className="relative overflow-hidden py-20 sm:py-24 lg:py-28"
          style={{
            background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: {} }}
        >
          {/* Subtle white grid overlay — matches Promise section feel */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),' +
                ' linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />

          <div className="relative max-w-3xl mx-auto px-5 lg:px-8 text-center">
            <motion.h2
              className="font-black leading-[1.05] tracking-tight text-white mb-5"
              style={{
                fontSize: 'clamp(34px, 5vw, 56px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
              }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE_OUT_EXPO } },
              }}
            >
              Have a Question
              <br />
              About Your Warranty?
            </motion.h2>

            <motion.p
              className="leading-relaxed mx-auto mb-10 max-w-xl"
              style={{
                fontSize: 'clamp(15px, 1.3vw, 18px)',
                color: 'rgba(255, 255, 255, 0.88)',
              }}
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
              }}
            >
              Talk to the OpenbooQ team — we handle every warranty claim end-to-end, from manufacturer paperwork to crew dispatch.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center mb-6"
              variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: EASE_OUT_EXPO } },
              }}
            >
              <motion.button
                type="button"
                onClick={openChat}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="inline-flex items-center justify-center gap-2.5 px-6 sm:px-7 py-3.5 rounded-xl font-bold text-[15px] bg-white"
                style={{
                  color: '#0a1628',
                  boxShadow: '0 10px 24px rgba(10, 22, 40, 0.16)',
                }}
              >
                <MessageCircle size={18} strokeWidth={2.4} aria-hidden="true" />
                Contact Warranty Support
              </motion.button>

              <motion.a
                href="tel:9379108791"
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="inline-flex items-center justify-center gap-2.5 px-6 sm:px-7 py-3.5 rounded-xl font-bold text-[15px] text-white"
                style={{
                  border: '1.5px solid rgba(255, 255, 255, 0.85)',
                  background: 'transparent',
                }}
              >
                <Phone size={18} strokeWidth={2.4} aria-hidden="true" />
                Call (937) 910-8791
              </motion.a>
            </motion.div>

            <motion.p
              className="text-sm"
              style={{ color: 'rgba(255, 255, 255, 0.78)' }}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { duration: 0.5, delay: 0.28, ease: EASE_OUT_EXPO } },
              }}
            >
              One number. End-to-end. No finger-pointing.
            </motion.p>
          </div>
        </motion.section>

        <Footer />
      </div>
    </>
  );
};

export default WarrantyPage;